'use client';

import { useEffect } from 'react';

/**
 * Hoists sidenotes referenced inside tables out into the margin gutter.
 *
 * A print sidenote is a right float pulled into the gutter by a negative
 * margin (print.css). That works from body prose, but a table cell is its own
 * formatting context: a float can never escape the <td>, so the note prints
 * on top of the cell text instead of in the margin (e.g. the "Other plans
 * that are competitive with Plan A" table, Appendix J of the book).
 *
 * Fix, in the page like PrintTextPrep so plain CMD+P gets it too: after
 * render, move each in-table note body out to just before the table's
 * top-level block, where it floats into the gutter beside the table. The
 * in-cell marker (<label class="sidenote-number">) stays put, so the CSS
 * sidenote counter still increments in reading order and every other note
 * keeps its number. The moved body, though, now sits BEFORE its marker's
 * increment and counter() would show the previous note's number — so its
 * true number is stamped as data-sidenote-number (rendered by print.css),
 * computed by counting markers in document order within the same
 * .markdown-content counter scope, reproducing exactly what the counter
 * shows on the in-cell marker.
 *
 * (A note nested inside a hoisted body would shift its own CSS-counter
 * number by moving; no such nesting exists in the content, and the failure
 * would be a cosmetic off-by-one on that inner note only.)
 */
export function PrintTableSidenotes() {
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.print-root');
    // Run once: StrictMode re-invokes effects, and after the moves the
    // markers' scope-relative positions no longer yield the same numbers.
    if (!root || root.dataset.tableSidenotesHoisted) return;
    root.dataset.tableSidenotesHoisted = 'true';

    const allMarkers = Array.from(
      root.querySelectorAll('label.sidenote-number'),
    );
    // The number the CSS counter gives `marker`: its 1-based position among
    // the markers sharing its counter scope (.markdown-content carries the
    // counter-reset — see tufte.css).
    const numberOf = (marker: Element): number => {
      const scope = marker.closest('.markdown-content');
      let n = 0;
      for (const other of allMarkers) {
        if (other.closest('.markdown-content') === scope) n++;
        if (other === marker) return n;
      }
      return n;
    };

    for (const note of Array.from(
      root.querySelectorAll<HTMLElement>('table .sidenote'),
    )) {
      // Each note renders as marker + right copy + left copy (printSidenote);
      // walk back over sibling copies to the shared marker.
      let marker = note.previousElementSibling;
      while (marker?.classList.contains('sidenote')) {
        marker = marker.previousElementSibling;
      }
      if (!marker?.classList.contains('sidenote-number')) continue;
      const table = note.closest('table');
      if (!table) continue;
      // The renderer wraps tables in a block wrapper (overflow div /
      // .table-wrapper); climb to the top-level block under the counter
      // scope and float the note just before it, beside the table.
      let anchor: Element = table;
      while (
        anchor.parentElement &&
        !anchor.parentElement.classList.contains('markdown-content')
      ) {
        anchor = anchor.parentElement;
      }
      const container = anchor.parentElement;
      if (!container) continue; // no counter scope above — leave it be
      note.dataset.sidenoteNumber = String(numberOf(marker));
      note.classList.add('sidenote-hoisted');
      container.insertBefore(note, anchor);
    }
  }, []);
  return null;
}
